import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { LoginCredentials, LoginResponse, User, ApiResponse } from '@/types'
import { RootState } from '@/app/store'
import { getAuthToken } from '@/utils/secureStorage'

// Base API configuration
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || '/api',
  prepareHeaders: (headers, { getState }) => {
    // Try to get token from Redux state first
    let token = (getState() as RootState).auth.token
    
    // If not in Redux state (e.g., after page refresh before rehydration), get from secure storage
    if (!token) {
      token = getAuthToken()
    }
    
    const language = (getState() as RootState).settings.settings.language
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    
    headers.set('lang', language)
    headers.set('Content-Type', 'application/json')
    headers.set('Accept', 'application/json')
    
    return headers
  },
})

// Auth API slice
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  tagTypes: ['User', 'Auth'],
  endpoints: (builder) => ({
    // Login with employee ID and password
    login: builder.mutation<ApiResponse<LoginResponse>, LoginCredentials>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['User', 'Auth'],
    }),
    
    // Login with PIN
    loginWithPin: builder.mutation<ApiResponse<LoginResponse>, string>({
      query: (pin) => ({
        url: '/auth/login-pin',
        method: 'POST',
        body: { pin },
      }),
      invalidatesTags: ['User', 'Auth'],
    }),
    
    // Logout
    logout: builder.mutation<ApiResponse<null>, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['User', 'Auth'],
    }),
    

  }),
})

export const {
  useLoginMutation,
  useLoginWithPinMutation,
  useLogoutMutation,
} = authApi
